// Faithful reproduction of Estimate_Add's totalling logic:
//   line total -> per-type subtotal -> per-type discount -> whole-estimate
//   subtotal -> whole-estimate discount -> sales tax -> total -> remaining.
// Used by the estimate editor to compute both the on-screen totals and the
// exact column values persisted back to the `estimates` row.
import type { EstimateItem } from '../types'

export const ESTIMATE_TYPES = ['Plants', 'Materials', 'Labor', 'Rental', 'Hardgoods'] as const
export type EstimateType = (typeof ESTIMATE_TYPES)[number]

// "Hidden Cost" rolls into Labor for subtotal purposes (legacy behaviour).
export function typeBucket(t: string | null | undefined): string {
  if (t === 'Hidden Cost') return 'Labor'
  return t ?? 'Other'
}

export function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

// Per-line total: qty * price, less the line's % then $ discount.
export function lineTotal(it: Pick<EstimateItem, 'qty' | 'price' | 'discount_percent' | 'discount_dollar'>): number {
  const gross = (it.qty ?? 0) * (it.price ?? 0)
  const afterPct = gross * (1 - (it.discount_percent ?? 0) / 100)
  return round2(afterPct - (it.discount_dollar ?? 0))
}

export interface TypeDiscounts {
  plant_p: number; plant_d: number
  material_p: number; material_d: number
  labor_p: number; labor_d: number
  rental_p: number; rental_d: number
}

export interface EstimateTotalsInput {
  items: Array<Pick<EstimateItem, 'item_type' | 'qty' | 'price' | 'cost' | 'discount_percent' | 'discount_dollar'>>
  discounts: TypeDiscounts
  wholeDiscountP: number
  wholeDiscountD: number
  taxPercent: number
  isNonTaxable: boolean
  deposit: number
}

export interface EstimateTotals {
  typeGross: Record<string, number>
  typeDiscounted: Record<string, number>
  subtotal: number          // sum of discounted type totals (whole_estimate)
  wholeDiscounted: number   // after whole-estimate discount (disc_whole_estimate)
  taxableBase: number
  taxAmount: number
  total: number
  deposit: number
  remaining: number
  cost: number
}

function discForType(t: string, d: TypeDiscounts): { p: number; dollar: number } {
  switch (t) {
    case 'Plants': return { p: d.plant_p, dollar: d.plant_d }
    case 'Materials': return { p: d.material_p, dollar: d.material_d }
    case 'Labor': return { p: d.labor_p, dollar: d.labor_d }
    case 'Rental': return { p: d.rental_p, dollar: d.rental_d }
    default: return { p: 0, dollar: 0 }
  }
}

export function computeEstimateTotals(input: EstimateTotalsInput): EstimateTotals {
  const { items, discounts, wholeDiscountP, wholeDiscountD, taxPercent, isNonTaxable, deposit } = input

  const typeGross: Record<string, number> = {}
  let cost = 0
  for (const it of items) {
    const b = typeBucket(it.item_type)
    typeGross[b] = round2((typeGross[b] ?? 0) + lineTotal(it))
    cost += (it.cost ?? 0) * (it.qty ?? 0)
  }

  const typeDiscounted: Record<string, number> = {}
  for (const t of Object.keys(typeGross)) {
    const { p, dollar } = discForType(t, discounts)
    typeDiscounted[t] = round2(typeGross[t] * (1 - p / 100) - dollar)
  }

  const subtotal = round2(Object.values(typeDiscounted).reduce((s, v) => s + v, 0))
  const wholeDiscounted = round2(subtotal * (1 - wholeDiscountP / 100) - wholeDiscountD)

  // Sales tax: labor is not taxed (faithful to landscaping invoicing); the
  // whole-estimate discount is applied proportionally to the taxable share.
  const laborDisc = typeDiscounted['Labor'] ?? 0
  const taxableShare = subtotal > 0 ? (subtotal - laborDisc) / subtotal : 0
  const taxableBase = isNonTaxable ? 0 : round2(wholeDiscounted * taxableShare)
  const taxAmount = round2(taxableBase * (taxPercent / 100))

  const total = round2(wholeDiscounted + taxAmount)
  const remaining = round2(total - deposit)

  return {
    typeGross,
    typeDiscounted,
    subtotal,
    wholeDiscounted,
    taxableBase,
    taxAmount,
    total,
    deposit: round2(deposit),
    remaining,
    cost: round2(cost),
  }
}
